import { useCallback, useMemo } from 'react';
import { useTranslations } from 'js/hooks/i18n';
import { func, money, range, ascending } from 'js/utils/Validation/validationRules';
import { TFormFieldBetLimit, TFormFieldLossLimit } from 'js/components/common/Form/types';
import { RG_LIMIT_PREDEFINED_TYPE } from 'js/hooks/socket/hooks/useGetRGConfiguration/constants';
import {
    TValidationMethodConfig,
    IValidationMethodConfigMoney,
    IValidationMethodConfigFunc,
    TValidationMethodConfigRange,
    IValidationMethodConfigAscending
} from 'js/utils/Validation/types';

import { useLimitsConfig } from './useLimitsConfig';
import { IMS_UNLIMITED_AMOUNT, UNLIMITED_AMOUNT } from '../constants';

export const useLimitsValidationConfig = (field?: TFormFieldBetLimit | TFormFieldLossLimit) => {
    const limitsConfig = useLimitsConfig(field);
    const type = field?.type === 'betLimits' ? 'bet' : 'loss';
    const translate = useTranslations('playtech.mobile.system.tooltip.');
    const {
        isSessionEnabled, isDailyEnabled, isWeeklyEnabled, isMonthlyEnabled,
        mandatory, noLimitsAllowed, predefinedType, predefinedLimits
    } = limitsConfig || {};

    const validateLimit = useCallback((
        rawValue: string, formValues: Record<string, string>
    ): string[] | undefined => {
        if (!field) {
            return;
        }

        const { imsFieldSession, imsFieldDaily, imsFieldWeekly, imsFieldMonthly } = field;
        const values = {
            ...isSessionEnabled && { [imsFieldSession]: formValues[imsFieldSession] },
            ...isDailyEnabled && { [imsFieldDaily]: formValues[imsFieldDaily] },
            ...isWeeklyEnabled && { [imsFieldWeekly]: formValues[imsFieldWeekly] },
            ...isMonthlyEnabled && { [imsFieldMonthly]: formValues[imsFieldMonthly] }
        };

        const value = Number(String(rawValue).replace(',', '.'));
        const areAllEmpty = Object.values(values)
            .map(val => Number(String(val).replace(',', '.')))
            .every(val => val === 0);

        if (mandatory && areAllEmpty) {
            return [translate(`limits.${type}LimitRequired`)];
        }

        if (mandatory && value === 0) {
            return [translate(`limits.all${type}LimitsRequired`)];
        }

        if (Number.isNaN(value)) {
            return [translate('amount')];
        }
    }, [
        translate, field, type, mandatory,
        isSessionEnabled, isDailyEnabled, isMonthlyEnabled, isWeeklyEnabled
    ]);

    return useMemo<Record<string, TValidationMethodConfig[]>>(() => {
        if (!field || !limitsConfig) {
            return {};
        }

        const { imsFieldSession, imsFieldDaily, imsFieldWeekly, imsFieldMonthly } = field;
        const formattedAllowedLimits = (predefinedLimits || [])
            .filter(limit => limit !== IMS_UNLIMITED_AMOUNT)
            .map(limit => Number(limit));
        const min = Math.min(...formattedAllowedLimits);
        const max = Math.max(...formattedAllowedLimits);
        const isRangeIncluded = predefinedType === RG_LIMIT_PREDEFINED_TYPE.ENABLED && formattedAllowedLimits.length;

        const rangeValidation = range({
            min,
            max,
            exceptions: noLimitsAllowed ? [0, UNLIMITED_AMOUNT] : [0],
            error: mandatory
                ? translate('enabledType', [min, max])
                : translate('enabledType.empty', [min, max])
        }) as TValidationMethodConfigRange;

        const getAscendingValidation = (period: string): IValidationMethodConfigAscending => ascending({
            exceptions: ['0'],
            error: translate(`limits.error.${period}Limit`),
            fields: [
                isSessionEnabled && imsFieldSession,
                isDailyEnabled && imsFieldDaily,
                isWeeklyEnabled && imsFieldWeekly,
                isMonthlyEnabled && imsFieldMonthly
            ].filter(Boolean)
        }) as IValidationMethodConfigAscending;

        return {
            ...isSessionEnabled && {
                [imsFieldSession]: [
                    money({ isZeroAccepted: true }) as IValidationMethodConfigMoney,
                    func({ fn: validateLimit }) as IValidationMethodConfigFunc,
                    ...isRangeIncluded ? [rangeValidation] : [],
                    getAscendingValidation('session')
                ]
            },
            ...isDailyEnabled && {
                [imsFieldDaily]: [
                    money({ isZeroAccepted: true }) as IValidationMethodConfigMoney,
                    func({ fn: validateLimit }) as IValidationMethodConfigFunc,
                    ...isRangeIncluded ? [rangeValidation] : [],
                    getAscendingValidation('day')
                ]
            },
            ...isWeeklyEnabled && {
                [imsFieldWeekly]: [
                    money({ isZeroAccepted: true }) as IValidationMethodConfigMoney,
                    func({ fn: validateLimit }) as IValidationMethodConfigFunc,
                    ...isRangeIncluded ? [rangeValidation] : [],
                    getAscendingValidation('week')
                ]
            },
            ...isMonthlyEnabled && {
                [imsFieldMonthly]: [
                    money({ isZeroAccepted: true }) as IValidationMethodConfigMoney,
                    func({ fn: validateLimit }) as IValidationMethodConfigFunc,
                    ...isRangeIncluded ? [rangeValidation] : [],
                    getAscendingValidation('month')
                ]
            }
        };
    }, [
        translate, field, limitsConfig, validateLimit,
        predefinedType, noLimitsAllowed, mandatory, predefinedLimits,
        isSessionEnabled, isDailyEnabled, isWeeklyEnabled, isMonthlyEnabled
    ]);
};
